'use client';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { FAVORITES_COPY } from '@eliadmin/core/src/components/dlz/account/favorites/favorites-copy';
import {
  FAVORITES_SORTS,
  type FavoritesSort as Sort,
} from '@eliadmin/core/src/components/dlz/account/favorites/favorites';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Figma „Sort“ (60325:21137): „Zoradiť:“ in DLZ/Body/S text/tertiary, the current order in Body/S
 * Medium and the 16 px filled caret; opens the options like the sort of the reviews.
 */
export function FavoritesSort({
  value,
  onChange,
  className,
}: {
  value: Sort;
  onChange: (sort: Sort) => void;
  className?: string;
}) {
  const { t } = useTranslations();
  const options = FAVORITES_SORTS.map(option => ({
    value: option.value,
    label: 'label' in option ? option.label : t(option.textKey),
  }));
  const current = options.find(option => option.value === value) ?? options[0];

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={cn(
          'dlz-body-s -m-1 flex items-center gap-1.5 rounded-lg p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]',
          className,
        )}
      >
        <span className="text-[#6B7079]">{FAVORITES_COPY.sort.label}</span>
        <span className="font-semibold text-[#0F1012]">{current.label}</span>
        <Icon name="caret-down-fill" size={16} className="text-[#0F1012]" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={8}
          className="z-50 min-w-[200px] rounded-[18px] border border-[#E2E4E7] bg-white p-2 shadow-dropdown data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none"
        >
          <DropdownMenu.RadioGroup
            value={current.value}
            onValueChange={next => {
              const option = options.find(item => item.value === next);
              if (option) onChange(option.value);
            }}
          >
            {options.map(option => (
              <DropdownMenu.RadioItem
                key={option.value}
                value={option.value}
                className="dlz-body-s flex h-10 cursor-pointer items-center justify-between gap-3 rounded-xl px-3 text-[#0F1012] outline-none data-[highlighted]:bg-[#F5F6F7] data-[state=checked]:font-semibold"
              >
                {option.label}
                <DropdownMenu.ItemIndicator>
                  <Icon name="check" size={16} />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
